import React, { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Settings as SettingsIcon, Save, Loader2, Bell, BellOff, Send } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported, currentPermission, subscribeAdmin, unsubscribeAdmin,
  fetchPushStatus, updatePushPrefs, sendTestPush,
} from "../../lib/adminPush";

const FIELDS = [
  { key: "commission_pct", type: "number", step: "0.1", en: "Commission %", ar: "نسبة العمولة %" },
  { key: "vat_pct", type: "number", step: "0.1", en: "VAT %", ar: "ضريبة القيمة المضافة %" },
  { key: "featured_listing_price_sar", type: "number", en: "Featured price (SAR)", ar: "سعر التمييز (ريال)" },
  { key: "premium_listing_price_sar", type: "number", en: "Premium price (SAR)", ar: "سعر بريميوم (ريال)" },
  { key: "max_images_per_listing", type: "number", en: "Max images/listing", ar: "أقصى صور/إعلان" },
  { key: "min_listing_images", type: "number", en: "Min images/listing", ar: "أدنى صور/إعلان" },
  { key: "max_listing_price_sar", type: "number", en: "Max listing price (SAR)", ar: "أقصى سعر إعلان (ريال)" },
  { key: "support_email", type: "email", en: "Support email", ar: "بريد الدعم" },
  { key: "support_phone", type: "text", en: "Support phone", ar: "هاتف الدعم" },
  { key: "whatsapp_number", type: "text", en: "WhatsApp", ar: "واتساب" },
  { key: "currency", type: "text", en: "Currency", ar: "العملة" },
  { key: "payment_cleanup_retention_days", type: "number", en: "Payment cleanup retention (days, min 30)", ar: "مدة الاحتفاظ قبل تنظيف المدفوعات غير المحصّلة (يوم، 30 كحد أدنى)" },
];

const TOGGLES = [
  { key: "auctions_enabled", en: "Enable Auctions", ar: "تفعيل المزادات" },
  { key: "marine_module_enabled", en: "Enable Marine module", ar: "تفعيل قسم البحري" },
  { key: "rentals_module_enabled", en: "Enable Rentals", ar: "تفعيل التأجير" },
  { key: "auto_publish_enabled", en: "Allow auto-publishing", ar: "السماح بالنشر التلقائي" },
  { key: "kyc_required_for_dealers", en: "Require KYC for dealers", ar: "إلزام KYC للتجار" },
  // iter181 — the legacy Beta-Launch toggle has been migrated to the
  // dynamic Campaign Manager tab. This block was intentionally removed
  // so promotions live in a single place.
];

export default function SettingsTab() {
  const { lang } = useI18n();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/settings").then(({ data }) => setS(data));
  }, []);

  if (!s) return <div className="p-10 text-[#525252]"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...payload } = s;
      const { data } = await apiClient.put("/admin/settings", payload);
      setS(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const setKey = (k, v) => setS({ ...s, [k]: v });

  return (
    <div data-testid="admin-settings" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl flex items-center gap-2"><SettingsIcon className="w-5 h-5 text-[#8a6f2e]" /> {lang === "ar" ? "إعدادات المنصة" : "Platform settings"}</h2>
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saved ? (lang === "ar" ? "تم الحفظ" : "Saved") : (lang === "ar" ? "حفظ" : "Save")}
        </button>
      </div>

      <div className="surface rounded-xl p-6">
        <h3 className="font-display text-lg mb-4">{lang === "ar" ? "الأسعار والحدود" : "Pricing & limits"}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FIELDS.map(f => (
            <div key={f.key}>
              <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? f.ar : f.en}</label>
              <input
                type={f.type}
                step={f.step}
                className="input-dark w-full"
                value={s[f.key] ?? ""}
                onChange={(e) => setKey(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
                data-testid={`setting-${f.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="surface rounded-xl p-6">
        <h3 className="font-display text-lg mb-4">{lang === "ar" ? "تفعيل/تعطيل الوحدات" : "Module toggles"}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {TOGGLES.map(t => (
            <label key={t.key} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#0a0a0a]/[0.06] cursor-pointer hover:border-[#c9a55c]/40">
              <span className="text-sm">{lang === "ar" ? t.ar : t.en}</span>
              <input
                type="checkbox"
                checked={!!s[t.key]}
                onChange={(e) => setKey(t.key, e.target.checked)}
                data-testid={`toggle-${t.key}`}
                className="w-4 h-4 accent-[#c9a55c]"
              />
            </label>
          ))}
        </div>
      </div>

      <AdminPushSection lang={lang} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * Admin Web Push section — iter192.
 * Reuses the platform Service Worker's existing `push` handler and the
 * backend endpoints under /api/admin/push/*.
 * ═══════════════════════════════════════════════════════════════════ */
const PUSH_KINDS = [
  { key: "new_user",         ar: "تسجيل مستخدم جديد",   en: "New user registration" },
  { key: "new_business",     ar: "تسجيل نشاط تجاري",     en: "New business registration" },
  { key: "new_verification", ar: "تقديم طلب توثيق جديد", en: "New verification request" },
  // iter235 — three lifecycle events. Keys mirror push_service.KINDS.
  { key: "new_listing",      ar: "نشر إعلان جديد",       en: "New listing published" },
  { key: "listing_deleted",  ar: "حذف إعلان",            en: "Listing deleted" },
  { key: "user_deleted",     ar: "حذف حساب مستخدم",      en: "User account deleted" },
];

function AdminPushSection({ lang }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setStatus(await fetchPushStatus()); }
    catch (e) { console.error("push status", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (!pushSupported()) {
    return (
      <div className="surface rounded-xl p-6" data-testid="admin-push-section">
        <h3 className="font-display text-lg mb-2 inline-flex items-center gap-2">
          <BellOff className="w-5 h-5 text-[#8a6f2e]" />
          {lang === "ar" ? "إشعارات الويب الفورية" : "Web Push notifications"}
        </h3>
        <p className="text-sm text-[#8a6f2e]">
          {lang === "ar"
            ? "متصفحك لا يدعم إشعارات الويب. جرّب Chrome، Edge، أو Firefox الحديث."
            : "This browser does not support Web Push. Try recent Chrome, Edge, or Firefox."}
        </p>
      </div>
    );
  }

  const perm = currentPermission();
  const subscribed = !!status?.subscribed;

  const doEnable = async () => {
    setBusy(true);
    try {
      await subscribeAdmin();
      toast.success(lang === "ar" ? "تم تفعيل الإشعارات على هذا الجهاز." : "Push enabled on this device.");
      await load();
    } catch (e) {
      toast.error(e?.message || (lang === "ar" ? "تعذّر التفعيل" : "Failed to enable"));
    } finally { setBusy(false); }
  };

  const doDisable = async () => {
    setBusy(true);
    try {
      await unsubscribeAdmin();
      toast.success(lang === "ar" ? "تم إلغاء الاشتراك." : "Unsubscribed.");
      await load();
    } catch (e) {
      toast.error(e?.message || (lang === "ar" ? "فشل" : "Failed"));
    } finally { setBusy(false); }
  };

  const togglePref = async (key, value) => {
    setStatus((prev) => prev ? { ...prev, prefs: { ...prev.prefs, [key]: value } } : prev);
    try { await updatePushPrefs({ [key]: value }); }
    catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      await load();
    }
  };

  const doTest = async () => {
    setBusy(true);
    try {
      const { sent } = await sendTestPush();
      toast.success(lang === "ar"
        ? `تم إرسال ${sent} إشعار تجريبي إلى هذا الجهاز.`
        : `Sent ${sent} test notification(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="surface rounded-xl p-6" data-testid="admin-push-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-display text-lg inline-flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#8a6f2e]" />
          {lang === "ar" ? "إشعارات الويب الفورية" : "Web Push notifications"}
        </h3>
        <div className="flex items-center gap-2">
          {subscribed ? (
            <>
              <button onClick={doTest} disabled={busy} className="btn-ghost text-sm" data-testid="push-send-test">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {lang === "ar" ? "إشعار تجريبي" : "Send test"}
              </button>
              <button onClick={doDisable} disabled={busy}
                      className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-sm font-bold border border-red-300 text-red-800 hover:bg-red-50"
                      data-testid="push-disable">
                <BellOff className="w-4 h-4" />
                {lang === "ar" ? "إلغاء الاشتراك" : "Unsubscribe"}
              </button>
            </>
          ) : (
            <button onClick={doEnable} disabled={busy}
                    className="btn-primary" data-testid="push-enable">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
              {lang === "ar" ? "تفعيل الإشعارات على هذا الجهاز" : "Enable on this device"}
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-[#525252] mb-4">
        {lang === "ar"
          ? "يتم إرسال الإشعارات حتى عند إغلاق التبويب — تظهر في مركز إشعارات النظام. تحتاج تفعيلاً مرة واحدة لكل جهاز/متصفح."
          : "Notifications are delivered even when the tab is closed, via your OS notification center. Enable once per device/browser."}
      </p>

      {perm === "denied" && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-xs p-3 mb-3">
          {lang === "ar"
            ? "تم حظر الإشعارات لهذا الموقع في إعدادات المتصفح. افتح إعدادات الموقع واسمح بالإشعارات لإعادة التفعيل."
            : "Notifications are blocked in browser settings. Allow them in site settings to re-enable."}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {PUSH_KINDS.map((k) => {
          const enabled = !!status?.prefs?.[k.key];
          return (
            <label key={k.key}
                   className={`flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer ${subscribed ? "border-[#0a0a0a]/[0.06] hover:border-[#c9a55c]/40" : "border-[#0a0a0a]/[0.04] opacity-60"}`}>
              <span className="text-sm">{lang === "ar" ? k.ar : k.en}</span>
              <input
                type="checkbox"
                checked={enabled}
                disabled={!subscribed || loading}
                onChange={(e) => togglePref(k.key, e.target.checked)}
                data-testid={`push-pref-${k.key}`}
                className="w-4 h-4 accent-[#c9a55c]"
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}
